import React, { useEffect, useState } from 'react';
import { Button } from '../../../../../components/Button';
import classes from './kitchen-note-modal.module.scss';

const MAX_NOTE_LENGTH = 120;

export type KitchenNoteModalProps = {
  isOpen: boolean;
  /** product name shown in the modal subtitle (the line the note belongs to) */
  productName: string;
  /** the note currently saved on the line, hydrated into the textarea on open */
  initialNote: string | null;
  isSaving?: boolean;
  onClose: () => void;
  /** receives the trimmed note, or null when cleared */
  onConfirm: (note: string | null) => void;
};

const KitchenNoteModalComponent: React.FC<KitchenNoteModalProps> = ({
  isOpen,
  productName,
  initialNote,
  isSaving = false,
  onClose,
  onConfirm,
}) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      setValue(initialNote ?? '');
    }
  }, [isOpen, initialNote]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const trimmed = value.trim();
    onConfirm(trimmed.length > 0 ? trimmed : null);
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label="הערת מטבח">
      <div className={classes.modal}>
        <div className={classes.header}>
          <h2 className={classes.title}>הערת מטבח</h2>
          {productName ? <span className={classes.subtitle}>{productName}</span> : null}
        </div>

        <div className={classes.body}>
          <div className={classes.fieldHeader}>
            <span className={classes.counter} data-testid="kitchen-note-counter">
              {MAX_NOTE_LENGTH} / {value.length}
            </span>
            <span className={classes.fieldLabel}>הערה חופשית</span>
          </div>
          <textarea
            className={classes.textarea}
            data-testid="kitchen-note-textarea"
            dir="rtl"
            value={value}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="הקלד הערה למטבח..."
            onChange={(event) => setValue(event.target.value.slice(0, MAX_NOTE_LENGTH))}
            autoFocus
          />
        </div>

        <div className={classes.actions}>
          <Button
            variant="cancel"
            fullWidth
            dataTestId="kitchen-note-cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            ביטול
          </Button>
          <Button
            variant="confirm"
            fullWidth
            dataTestId="kitchen-note-confirm"
            onClick={handleConfirm}
            disabled={isSaving}
          >
            {isSaving ? 'שומר...' : 'אישור'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const KitchenNoteModal = React.memo(KitchenNoteModalComponent);
